"use client";

import clsx from "clsx";
import React from "react";
import styles from "./DateTimeInput.module.scss";
import { type TimeOfDay, formatTime, parseTime, sameTime, toSeconds } from "./dateTimeFormat";

/**
 * A shortcut listed above the stepped times. Consumers can define their own; TIME_PRESETS is a set
 * of ready-made ones. `time` accepts a TimeOfDay or any text parseTime accepts ("9am", "17:30").
 */
export interface TimePreset {
  label: string;
  time: TimeOfDay | string;
}

export const TIME_PRESETS = {
  startOfDay: { label: "Start of day", time: { hours: 0, minutes: 0, seconds: 0 } },
  nineAm: { label: "9am", time: { hours: 9, minutes: 0, seconds: 0 } },
  midday: { label: "Midday", time: { hours: 12, minutes: 0, seconds: 0 } },
  fivePm: { label: "5pm", time: { hours: 17, minutes: 0, seconds: 0 } },
  endOfDay: { label: "End of day", time: { hours: 23, minutes: 59, seconds: 59 } },
} satisfies Record<string, TimePreset & { time: TimeOfDay }>;

export type TimeOption = { time: TimeOfDay; label?: string };

export const resolvePresetTime = (time: TimeOfDay | string) => (typeof time === "string" ? parseTime(time) : time);

/**
 * Presets first, then one option every `stepMinutes`. A null step lists only the presets.
 * Presets whose time does not parse are dropped.
 */
export const buildTimeOptions = (stepMinutes: number | null, presets: TimePreset[] = []): TimeOption[] => {
  const presetOptions = presets.flatMap((preset) => {
    const time = resolvePresetTime(preset.time);
    return time ? [{ time, label: preset.label }] : [];
  });
  if (stepMinutes === null) return presetOptions;

  const step = Math.min(Math.max(Math.round(stepMinutes), 1), 24 * 60);
  const steps: TimeOption[] = Array.from({ length: Math.ceil((24 * 60) / step) }, (_, index) => ({
    time: { hours: Math.floor((index * step) / 60), minutes: (index * step) % 60, seconds: 0 },
  }));
  return [...presetOptions, ...steps];
};

/** Index of the option to highlight for a value: an exact match, else the nearest stepped time. */
export const findTimeOptionIndex = (options: TimeOption[], time: TimeOfDay | null) => {
  if (!options.length) return -1;
  const target = time ?? { hours: 9, minutes: 0 };
  // Prefer the stepped time over a preset with the same time: presets are shortcuts, the steps are the main list.
  const exactStep = options.findIndex((option) => !option.label && sameTime(option.time, target));
  if (exactStep >= 0) return exactStep;
  const exactPreset = options.findIndex((option) => sameTime(option.time, target));
  if (exactPreset >= 0) return exactPreset;

  let nearest = -1;
  let nearestDistance = Number.POSITIVE_INFINITY;
  options.forEach((option, index) => {
    if (option.label) return;
    const distance = Math.abs(toSeconds(option.time) - toSeconds(target));
    if (distance < nearestDistance) {
      nearest = index;
      nearestDistance = distance;
    }
  });
  return nearest;
};

export const getTimeOptionId = (listId: string, index: number) => `${listId}-option-${index}`;

/**
 * Shared Up/Down/Home/End/PageUp/PageDown handling for a time listbox. Returns the next active
 * index, or null when the key is not a navigation key.
 */
export const getNextTimeOptionIndex = (key: string, activeIndex: number, count: number) => {
  const last = count - 1;
  switch (key) {
    case "ArrowDown":
      return activeIndex < 0 ? 0 : Math.min(activeIndex + 1, last);
    case "ArrowUp":
      return activeIndex < 0 ? last : Math.max(activeIndex - 1, 0);
    case "PageDown":
      return Math.min(Math.max(activeIndex, 0) + 4, last);
    case "PageUp":
      return Math.max(activeIndex - 4, 0);
    case "Home":
      return 0;
    case "End":
      return last;
    default:
      return null;
  }
};

/**
 * Scrolls an option's nearest scrollable ancestor (not the page) so the option is at the top
 * ("start") or just inside the visible area ("nearest").
 */
const scrollOptionIntoView = (element: HTMLElement, align: "start" | "nearest") => {
  let container = element.parentElement;
  while (container && container.scrollHeight <= container.clientHeight) container = container.parentElement;
  // Only scroll the list's own columns, never the page. They are positioned (see .timeList), so
  // offsetTop is measured from them and is unaffected by the panel's open animation transform.
  if (!container || element.offsetParent !== container) return;

  const padding = Number.parseFloat(getComputedStyle(container).paddingTop) || 0;
  const top = element.offsetTop;
  if (align === "start") {
    container.scrollTop = top - padding;
    return;
  }
  const bottom = top + element.offsetHeight;
  if (top - padding < container.scrollTop) container.scrollTop = top - padding;
  else if (bottom + padding > container.scrollTop + container.clientHeight) {
    container.scrollTop = bottom + padding - container.clientHeight;
  }
};

/** "side": presets in their own column left of the stepped times. "stacked": presets above them. */
export type TimePresetLayout = "side" | "stacked";

const isSideBySide = (options: TimeOption[], layout: TimePresetLayout) =>
  layout === "side" && options.some((option) => option.label) && options.some((option) => !option.label);

/**
 * ArrowLeft / ArrowRight between the preset and time columns of a side-by-side list, landing on the
 * option nearest in time (e.g. 9am preset <-> 9:00). Returns null when the key does not apply.
 */
export const getColumnSwitchIndex = (
  key: string,
  activeIndex: number,
  options: TimeOption[],
  layout: TimePresetLayout,
) => {
  if ((key !== "ArrowLeft" && key !== "ArrowRight") || !isSideBySide(options, layout)) return null;
  const active = options[activeIndex];
  if (!active) return null;
  const toPresets = key === "ArrowLeft";
  if (Boolean(active.label) === toPresets) return null;

  let nearest = -1;
  let nearestDistance = Number.POSITIVE_INFINITY;
  options.forEach((option, index) => {
    if (Boolean(option.label) !== toPresets) return;
    const distance = Math.abs(toSeconds(option.time) - toSeconds(active.time));
    if (distance < nearestDistance) {
      nearest = index;
      nearestDistance = distance;
    }
  });
  return nearest >= 0 ? nearest : null;
};

/**
 * Up/Down/Home/End/PageUp/PageDown for a time list in the given layout. Side by side, movement stays
 * inside the active column (presets or times); stacked, it runs through the whole list.
 */
export const getNextTimeOptionIndexInLayout = (
  key: string,
  activeIndex: number,
  options: TimeOption[],
  layout: TimePresetLayout,
) => {
  if (!isSideBySide(options, layout) || activeIndex < 0) {
    return getNextTimeOptionIndex(key, activeIndex, options.length);
  }
  const presetCount = options.filter((option) => option.label).length;
  const inPresets = activeIndex < presetCount;
  const start = inPresets ? 0 : presetCount;
  const count = inPresets ? presetCount : options.length - presetCount;
  const next = getNextTimeOptionIndex(key, activeIndex - start, count);
  return next === null ? null : start + next;
};

export interface TimeListProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "onSelect"> {
  id: string;
  options: TimeOption[];
  value: TimeOfDay | null;
  activeIndex: number;
  onActiveIndexChange: (index: number) => void;
  onSelect: (time: TimeOfDay) => void;
  hour12: boolean;
  showSeconds?: boolean;
  presetLayout?: TimePresetLayout;
}

/**
 * Listbox of times. Focus normally stays on a controlling element (a combobox input, or the list
 * itself when it is given `tabIndex`), which points at the active option via
 * `aria-activedescendant`. Options are never focused directly.
 */
export const TimeList = ({
  id,
  options,
  value,
  activeIndex,
  onActiveIndexChange,
  onSelect,
  hour12,
  showSeconds,
  presetLayout = "side",
  className,
  ...props
}: TimeListProps) => {
  const listRef = React.useRef<HTMLDivElement | null>(null);
  const firstStepIndex = options.findIndex((option) => !option.label);
  const sideBySide = isSideBySide(options, presetLayout);

  // The list mounts each time the popover opens. On that first pass the highlighted option is
  // scrolled to the top of its column (so 9am shows with the rest of the day below it); after that,
  // keyboard movement only scrolls as far as needed to keep the option visible.
  const initialScrollDoneRef = React.useRef(false);
  React.useEffect(() => {
    if (activeIndex < 0) return;
    const getOption = (index: number) => listRef.current?.ownerDocument.getElementById(getTimeOptionId(id, index));
    const element = getOption(activeIndex);
    if (!element) return;

    if (initialScrollDoneRef.current) {
      scrollOptionIntoView(element, "nearest");
      return;
    }
    initialScrollDoneRef.current = true;
    scrollOptionIntoView(element, "start");
    // A highlighted preset sits in its own column; line the times column up on the nearest time too.
    if (sideBySide && options[activeIndex]?.label) {
      const nearestStep = findTimeOptionIndex(
        options.filter((option) => !option.label),
        options[activeIndex].time,
      );
      const stepElement = getOption(nearestStep + options.filter((option) => option.label).length);
      if (stepElement) scrollOptionIntoView(stepElement, "start");
    }
  }, [activeIndex, id, options, sideBySide]);

  const renderOption = (option: TimeOption, index: number) => {
    const selected = sameTime(option.time, value);
    const formatted = formatTime(option.time, { hour12, showSeconds });
    return (
      // biome-ignore lint/a11y/useFocusableInteractive: options are referenced via aria-activedescendant, never focused
      // biome-ignore lint/a11y/useKeyWithClickEvents: keyboard selection is handled by the controlling element
      <div
        key={`${option.label ?? "step"}-${formatted}`}
        id={getTimeOptionId(id, index)}
        // biome-ignore lint/a11y/useSemanticElements: see the listbox note below
        role="option"
        aria-selected={selected}
        data-active={index === activeIndex || undefined}
        data-separator={!sideBySide && index === firstStepIndex && index > 0 ? "true" : undefined}
        className={clsx(styles.timeOption, option.label && styles.timePreset)}
        onMouseMove={() => {
          if (index !== activeIndex) onActiveIndexChange(index);
        }}
        onClick={() => onSelect(option.time)}
      >
        {option.label ? (
          <>
            <span>{option.label}</span>
            <span className={styles.timePresetValue}>{formatted}</span>
          </>
        ) : (
          formatted
        )}
      </div>
    );
  };

  const indexed = options.map((option, index) => ({ option, index }));

  return (
    // biome-ignore lint/a11y/useFocusableInteractive: focus stays on the controlling combobox (or the caller passes tabIndex), per the ARIA combobox pattern
    <div
      ref={listRef}
      id={id}
      // biome-ignore lint/a11y/useSemanticElements: <select> cannot be a popup listbox driven by aria-activedescendant
      role="listbox"
      aria-label="Times"
      data-layout={sideBySide ? "side" : "stacked"}
      className={clsx(styles.timeList, className)}
      {...props}
    >
      {sideBySide ? (
        <>
          <div
            // biome-ignore lint/a11y/useSemanticElements: an option group inside a listbox, not a form fieldset
            role="group"
            aria-labelledby={`${id}-presets-label`}
            className={styles.timeListPresets}
          >
            <div
              id={`${id}-presets-label`}
              role="presentation"
              className={styles.timeListHeader}
            >
              Presets
            </div>
            {indexed.filter(({ option }) => option.label).map(({ option, index }) => renderOption(option, index))}
          </div>
          <div
            // biome-ignore lint/a11y/useSemanticElements: an option group inside a listbox, not a form fieldset
            role="group"
            aria-label="Times"
            className={styles.timeListSteps}
          >
            {indexed.filter(({ option }) => !option.label).map(({ option, index }) => renderOption(option, index))}
          </div>
        </>
      ) : (
        options.map(renderOption)
      )}
    </div>
  );
};
